import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import { useTranslation } from "../../../i18n";
import { createTicket, fetchMyTickets, replyToTicket } from "../supportApi";
import {
  TicketCategoryBadge,
  TicketMessages,
  TicketStatusBadge,
  formatTicketDate,
} from "../../../shared/components/support/TicketThread";

const ClientSupportPage = () => {
  const { t, locale } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [category, setCategory] = useState("support");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Prefill from in-app links (e.g. the Enterprise request from the
  // location picker). Consumed once, then cleared from history state.
  useEffect(() => {
    const preset = location.state;
    if (!preset || (!preset.presetSubject && !preset.presetMessage)) return;
    if (preset.presetCategory) setCategory(preset.presetCategory);
    if (preset.presetSubject) setSubject(preset.presetSubject);
    if (preset.presetMessage) setMessage(preset.presetMessage);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replySending, setReplySending] = useState(false);

  const loadTickets = useCallback(async () => {
    try {
      const list = await fetchMyTickets();
      setTickets(list);
      setError("");
    } catch {
      setError(t("support.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const faq = useMemo(
    () => [
      { q: t("support.faq1q"), a: t("support.faq1a") },
      { q: t("support.faq2q"), a: t("support.faq2a") },
      { q: t("support.faq3q"), a: t("support.faq3a") },
    ],
    [t]
  );

  const categoryOptions = useMemo(
    () => [
      { value: "support", label: t("support.categorySupport") },
      { value: "feedback", label: t("support.categoryFeedback") },
    ],
    [t]
  );
  const isFeedback = category === "feedback";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!subject.trim() || !message.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const ticket = await createTicket({
        subject: subject.trim(),
        message: message.trim(),
        category,
      });
      setTickets((prev) => [ticket, ...prev]);
      setSubject("");
      setMessage("");
      setSubmitted(true);
      setExpandedId(ticket.id);
      window.setTimeout(() => setSubmitted(false), 3000);
    } catch {
      setError(t("support.submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (ticketId) => {
    const draft = (replyDrafts[ticketId] || "").trim();
    if (!draft || replySending) return;
    setReplySending(true);
    try {
      const updated = await replyToTicket(ticketId, draft);
      setTickets((prev) => prev.map((tk) => (tk.id === ticketId ? updated : tk)));
      setReplyDrafts((prev) => ({ ...prev, [ticketId]: "" }));
    } catch {
      setError(t("support.submitError"));
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className="bg-background font-body-md text-on-surface">
      <div className="mx-auto max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <h1 className="font-headline-lg text-headline-lg text-primary">{t("support.title")}</h1>
        <p className="mt-2 text-body-md text-on-surface-variant">{t("support.subtitle")}</p>

        <div className="mt-stack-lg grid gap-gutter lg:grid-cols-2">
          <form
            onSubmit={handleSubmit}
            className="tonal-card rounded-2xl border border-outline-variant/30 p-stack-lg"
          >
            <h2 className="font-headline-md text-primary">{t("support.submitRequest")}</h2>

            <div className="mt-4">
              <span className="text-label-sm text-on-surface-variant">
                {t("support.categoryLabel")}
              </span>
              <div className="mt-1">
                <EnterpriseFilterSelect
                  id="support-category"
                  value={category}
                  onChange={setCategory}
                  options={categoryOptions}
                  ariaLabel={t("support.categoryLabel")}
                />
              </div>
              <p className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-relaxed text-on-surface-variant">
                <MaterialIcon
                  name={isFeedback ? "lightbulb" : "info"}
                  className="mt-0.5 shrink-0 text-[14px] text-secondary"
                />
                {isFeedback
                  ? t("support.categoryHintFeedback")
                  : t("support.categoryHintSupport")}
              </p>
            </div>

            <label className="mt-4 block">
              <span className="text-label-sm text-on-surface-variant">{t("support.subject")}</span>
              <input
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                className="mt-1 w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-3 outline-none focus:ring-2 focus:ring-secondary"
                placeholder={
                  isFeedback
                    ? t("support.subjectPlaceholderFeedback")
                    : t("support.subjectPlaceholder")
                }
              />
            </label>
            <label className="mt-4 block">
              <span className="text-label-sm text-on-surface-variant">{t("support.message")}</span>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={5}
                className="mt-1 w-full resize-y rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-3 outline-none focus:ring-2 focus:ring-secondary"
                placeholder={
                  isFeedback
                    ? t("support.messagePlaceholderFeedback")
                    : t("support.messagePlaceholder")
                }
              />
            </label>
            {error ? (
              <p className="mt-3 rounded-lg bg-error-container px-4 py-2.5 text-label-sm text-on-error-container">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-label-sm font-medium text-on-primary disabled:opacity-60"
            >
              {submitted ? (
                <>
                  <MaterialIcon name="check" filled />
                  {isFeedback
                    ? t("support.feedbackSubmitted")
                    : t("support.ticketSubmitted")}
                </>
              ) : (
                <>
                  {isFeedback ? t("support.submitFeedback") : t("support.submitTicket")}
                  <MaterialIcon name="send" />
                </>
              )}
            </button>
          </form>

          <div className="space-y-4">
            <div className="rounded-2xl border border-secondary/20 bg-secondary-container/30 p-stack-lg">
              <MaterialIcon name="support_agent" className="mb-2 text-secondary" filled />
              <p className="font-headline-md text-primary">{t("support.prioritySupport")}</p>
              <p className="mt-1 text-[12px] text-on-surface-variant">
                {t("support.prioritySupportHint")}
              </p>
            </div>

            <div className="tonal-card rounded-2xl border border-outline-variant/30 p-stack-lg">
              <h2 className="font-headline-md text-primary">{t("support.faqTitle")}</h2>
              <ul className="mt-4 space-y-4">
                {faq.map((item) => (
                  <li key={item.q}>
                    <p className="font-medium text-primary">{item.q}</p>
                    <p className="mt-1 text-[12px] leading-relaxed text-on-surface-variant">
                      {item.a}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-stack-lg">
          <h2 className="font-headline-md text-primary">{t("support.yourTickets")}</h2>
          {loading ? null : tickets.length === 0 ? (
            <p className="mt-3 font-body-md text-on-surface-variant">{t("support.noTickets")}</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {tickets.map((ticket) => {
                const isExpanded = expandedId === ticket.id;
                return (
                  <li
                    key={ticket.id}
                    className="tonal-card overflow-hidden rounded-xl border border-outline-variant/30"
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                      className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <MaterialIcon
                          name={isExpanded ? "expand_more" : "chevron_right"}
                          className="shrink-0 text-[18px] text-on-surface-variant"
                        />
                        <span className="truncate font-medium text-primary">{ticket.subject}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="font-label-sm text-outline">
                          {formatTicketDate(ticket.createdAt, locale)}
                        </span>
                        <TicketCategoryBadge category={ticket.category} t={t} />
                        <TicketStatusBadge status={ticket.status} t={t} />
                      </span>
                    </button>

                    {isExpanded ? (
                      <div className="border-t border-outline-variant/30 bg-surface-container-low/40 p-4">
                        <TicketMessages
                          messages={ticket.messages}
                          t={t}
                          locale={locale}
                          perspective="client"
                        />
                        {ticket.status !== "closed" ? (
                          <div className="mt-4 flex items-end gap-2">
                            <textarea
                              value={replyDrafts[ticket.id] || ""}
                              onChange={(event) =>
                                setReplyDrafts((prev) => ({
                                  ...prev,
                                  [ticket.id]: event.target.value,
                                }))
                              }
                              rows={2}
                              className="w-full resize-y rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary"
                              placeholder={t("support.replyPlaceholder")}
                            />
                            <button
                              type="button"
                              disabled={replySending || !(replyDrafts[ticket.id] || "").trim()}
                              onClick={() => handleReply(ticket.id)}
                              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-label-sm font-medium text-on-primary disabled:opacity-50"
                            >
                              {t("support.replySend")}
                              <MaterialIcon name="send" className="text-[16px]" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientSupportPage;
